import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { collection, onSnapshot, doc, setDoc, deleteDoc, addDoc } from 'firebase/firestore';
import { useData } from '../DataProvider';
import { firestore } from '../../App';
import { Tag, TagType } from './tags-schema';
import { assert } from 'superstruct';

interface TagsContextProps {
  tags: TagType[] | null;
  addTag: (newTag: Omit<TagType, 'id'>) => Promise<string>;
  updateTag: (id: string, newTag: TagType) => Promise<void>;
  deleteTag: (id: string) => Promise<void>;
  loadingTags: boolean;
}

const TagsContext = createContext<TagsContextProps | undefined>(undefined);

export const useTags = (): TagsContextProps => {
  const context = useContext(TagsContext);
  if (!context) {
    throw new Error('useTags must be used within a TagsProvider');
  }
  return context;
};

interface TagsProviderProps {
  children: ReactNode;
}

export const TagsProvider: React.FC<TagsProviderProps> = ({ children }) => {
  const [tags, setTags] = useState<TagType[] | null>(null);
  const [loadingTags, setLoadingTags] = useState(true);
  const { permissions } = useData(); // Assuming useData provides user permissions

  useEffect(() => {
    const unsubscribe = onSnapshot(collection(firestore, 'tags'), (snapshot) => {
      const tagsList: TagType[] = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }) as TagType);

      setTags(tagsList);
      setLoadingTags(false);
    }, (err) => {
      console.error(err);
      setLoadingTags(false);
    });

    return () => unsubscribe();
  }, [firestore]);

  const addTag = async (newTag: Omit<TagType, 'id'>): Promise<string> => {
    try {
      // Create a temporary tag with a placeholder ID for validation
      const tempTag = { ...newTag, id: 'temp-id' } as TagType;
      assert(tempTag, Tag); // Validate the new tag
      
      const tagsCollection = collection(firestore, 'tags');
      const docRef = await addDoc(tagsCollection, newTag);
      return docRef.id;
    } catch (err) {
      throw new Error((err as Error).message);
    }
  };

  const updateTag = async (id: string, newTag: TagType): Promise<void> => {
    try {
      assert(newTag, Tag); // Validate the tag
      const tagDoc = doc(firestore, 'tags', id);
      await setDoc(tagDoc, newTag);
    } catch (err) {
      throw new Error((err as Error).message);
    }
  };

  const deleteTag = async (id: string): Promise<void> => {
    try {
      const tagDoc = doc(firestore, 'tags', id);
      await deleteDoc(tagDoc);
    } catch (err) {
      throw new Error((err as Error).message);
    }
  };

  return (
    <TagsContext.Provider value={{ tags, addTag, updateTag, deleteTag, loadingTags }}>
      {children}
    </TagsContext.Provider>
  );
}; 